import React from 'react';
import { Tooltip } from 'antd';
import { usePermissionCheck } from './usePermissionCheck';

/**
 * Higher-order function that adds permission checking to components
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {Object} options - Configuration options
 * @param {string} options.defaultTooltipMessage - Default tooltip message for disabled state
 * @param {boolean} options.useTooltip - Whether to wrap with tooltip (default: true)
 * @param {boolean} options.passDisabled - Whether to pass disabled prop to wrapped component (default: true)
 * @returns {React.Component} - Wrapped component with permission checking
 */
export const withPermission = (WrappedComponent, options = {}) => {
  const {
    defaultTooltipMessage = "You don't have permission to use this feature",
    useTooltip = true,
    passDisabled = true
  } = options;

  const PermissionedComponent = React.forwardRef(({ permissionCheck, permissionMessage, tooltipMessage, ...props }, ref) => {
    const { permissionDisabled } = usePermissionCheck(permissionCheck);
    
    const finalTooltipMessage = tooltipMessage || permissionMessage || defaultTooltipMessage;
    
    // If no permission and we should pass disabled
    if (permissionDisabled && passDisabled) {
      const componentProps = { ...props, disabled: true, ref };
      
      if (useTooltip) {
        return (
          <Tooltip title={finalTooltipMessage}>
            <WrappedComponent {...componentProps} />
          </Tooltip>
        );
      }
      
      return <WrappedComponent {...componentProps} />;
    }
    
    // If has permission, render normal component
    if (useTooltip && permissionDisabled) {
      return (
        <Tooltip title={finalTooltipMessage}>
          <WrappedComponent {...props} ref={ref} />
        </Tooltip>
      );
    }
    
    return <WrappedComponent {...props} ref={ref} />;
  });

  // Copy display name for debugging
  PermissionedComponent.displayName = `withPermission(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return PermissionedComponent;
}; 